import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import {
  intercomUnreadCount,
  isIntercomAvailable,
  setIntercomLauncherVisible,
  startIntercom
} from '@/utils/intercom'

/** How long to wait for the page to go idle before loading Intercom anyway. */
const IDLE_TIMEOUT_MS = 5000

interface LauncherGetters {
  isFreeTrial?: boolean
  isInApp?: boolean
}

/**
 * Whether to show Intercom's floating launcher as well as the header's chat button: only while
 * the organization is on a free trial or still setting up, when people get stuck most and a
 * person within reach matters most. Established organizations get a cleaner page.
 */
export function shouldShowFloatingLauncher (getters: LauncherGetters): boolean {
  return Boolean(getters.isFreeTrial || getters.isInApp)
}

/**
 * Whether this session should load Intercom at all. Not for bootstrapped org access (delegate or
 * system, as when a scheduled report renders headless): no one there signed in to the console,
 * so there is no one to log in to the Messenger as.
 */
export function shouldLoadIntercom (getters: { 'jwt/isBootstrappedOrgAccess'?: boolean }): boolean {
  return isIntercomAvailable() && !getters['jwt/isBootstrappedOrgAccess']
}

/**
 * Loads the Messenger in the background once the page is idle, so support replies show as an
 * unread count in the header rather than only by email, and shows the floating launcher to the
 * organizations it is meant for.
 */
export function useIntercomMessenger () {
  const store = useStore()
  // Reactive, because bootstrapped claims are set just after the store is created.
  const available = computed(() => shouldLoadIntercom(store.getters))

  watch(
    () => available.value && shouldShowFloatingLauncher(store.getters),
    visible => { if (available.value) setIntercomLauncherVisible(visible) },
    { immediate: true }
  )

  let cancelIdle: (() => void) | null = null
  onMounted(() => {
    cancelIdle = whenIdle(() => {
      if (available.value) startIntercom()
    })
  })
  onUnmounted(() => cancelIdle?.())

  return {
    available,
    unreadCount: computed(() => intercomUnreadCount.value)
  }
}

function whenIdle (callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS })
    return () => window.cancelIdleCallback(handle)
  }
  const handle = window.setTimeout(callback, IDLE_TIMEOUT_MS)
  return () => window.clearTimeout(handle)
}
